/**
 * Google Analytics 4 Event Tracking
 *
 * Funnel tracking:
 * 1. page_view (automatic) - Traffic initial
 * 2. file_added - Utilisateur ajoute des fichiers
 * 3. form_started - Utilisateur commence à remplir le formulaire
 * 4. form_submitted - Formulaire envoyé (upload vers webhook)
 * 5. account_created - Création de compte
 * 6. payment_initiated - Clic sur bouton paiement
 * 7. payment_completed - Paiement réussi
 *
 * A/B Testing:
 * - experiment_impression - User sees a variant
 * - All events include experiment_variant when available
 */

import { getCurrentVariant, EXPERIMENTS } from '@/hooks/useABTest';

// Vérifie si GA4 est chargé
const isGALoaded = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Lit le client_id GA4 depuis le cookie _ga (format "GA1.2.<id1>.<id2>" -> "<id1>.<id2>").
// Transmis au backend à la création de la session Stripe pour que le webhook puisse
// envoyer un événement "purchase" côté serveur rattaché au même visiteur (cf. analytics
// côté serveur dans server.py : /payment/create-session, /payment/confirm, /webhook/stripe).
export const getGaClientId = () => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.(\d+\.\d+)/);
  return match ? match[1] : null;
};

// Meta Pixel : envoie l'événement si le pixel est chargé.
// Le consentement est géré par Meta lui-même (fbq consent revoke/grant) :
// sans consentement, les événements sont bufferisés et jamais envoyés.
const fbqTrack = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', eventName, params);
  }
};

// Get current A/B test variant for landing page
const getLandingVariant = () => {
  return getCurrentVariant(EXPERIMENTS.LANDING_PAGE_HERO.id);
};

// Event 1: Page view (automatique avec GA4)
// Pas besoin de code spécifique

// Event 2: Ajout de fichier
export const trackFileAdded = (fileCount) => {
  if (!isGALoaded()) return;

  const variant = getLandingVariant();
  window.gtag('event', 'file_added', {
    event_category: 'Engagement',
    event_label: 'File Upload',
    value: fileCount,
    file_count: fileCount,
    ...(variant && { experiment_variant: variant }),
  });
};

// Event 3: Formulaire commencé
export const trackFormStarted = () => {
  if (!isGALoaded()) return;

  const variant = getLandingVariant();
  window.gtag('event', 'form_started', {
    event_category: 'Engagement',
    event_label: 'User started filling form',
    ...(variant && { experiment_variant: variant }),
  });
};

// Event 4: Formulaire soumis (upload vers webhook)
export const trackFormSubmitted = (fileCount, hasRecaptcha) => {
  fbqTrack('Lead');
  if (!isGALoaded()) return;

  const variant = getLandingVariant();
  window.gtag('event', 'form_submitted', {
    event_category: 'Conversion',
    event_label: 'Dossier Upload',
    value: fileCount,
    file_count: fileCount,
    has_recaptcha: hasRecaptcha,
    ...(variant && { experiment_variant: variant }),
  });
};

// Event 5: Création de compte
export const trackAccountCreated = (method = 'email') => {
  if (!isGALoaded()) return;

  window.gtag('event', 'sign_up', {
    method: method,
    event_category: 'Conversion',
    event_label: 'Account Created',
  });
};

// Event 6: Connexion
export const trackLogin = (method = 'email') => {
  if (!isGALoaded()) return;

  window.gtag('event', 'login', {
    method: method,
    event_category: 'Engagement',
  });
};

// Event 7: Paiement initié
export const trackPaymentInitiated = (value = 9.99, currency = 'EUR') => {
  fbqTrack('InitiateCheckout', { value, currency });
  if (!isGALoaded()) return;

  window.gtag('event', 'begin_checkout', {
    event_category: 'Ecommerce',
    event_label: 'Payment Button Clicked',
    value: value,
    currency: currency,
    items: [{
      item_id: 'strategy_unlock',
      item_name: 'Stratégie d\'action personnalisée',
      price: value,
      quantity: 1,
    }],
  });
};

// Event 8: Paiement réussi
export const trackPaymentCompleted = (value = 9.99, currency = 'EUR', transactionId = null) => {
  fbqTrack('Purchase', { value, currency });
  if (!isGALoaded()) return;

  window.gtag('event', 'purchase', {
    event_category: 'Ecommerce',
    event_label: 'Payment Successful',
    transaction_id: transactionId,
    value: value,
    currency: currency,
    items: [{
      item_id: 'strategy_unlock',
      item_name: 'Stratégie d\'action personnalisée',
      price: value,
      quantity: 1,
    }],
  });
};

// Event personnalisé: Consultation du score
export const trackScoreViewed = (score, isAnonymous) => {
  fbqTrack('ViewContent', { content_name: 'score_page' });
  if (!isGALoaded()) return;

  window.gtag('event', 'score_viewed', {
    event_category: 'Engagement',
    event_label: `Score: ${score}`,
    value: score,
    is_anonymous: isAnonymous,
  });
};

// Event personnalisé: Clic sur CTA "Créer un compte"
export const trackCTAClicked = (ctaName, location) => {
  if (!isGALoaded()) return;

  window.gtag('event', 'cta_clicked', {
    event_category: 'Engagement',
    event_label: ctaName,
    cta_location: location,
  });
};

// Event: Clic sur CTA Hero (A/B test)
export const trackHeroCTAClick = (ctaText) => {
  if (!isGALoaded()) return;

  const variant = getLandingVariant();
  window.gtag('event', 'hero_cta_click', {
    event_category: 'Hero',
    event_label: `Variant ${variant}: ${ctaText}`,
    variant: variant,
    cta_text: ctaText,
  });
};

export default {
  trackFileAdded,
  trackFormStarted,
  trackFormSubmitted,
  trackAccountCreated,
  trackLogin,
  trackPaymentInitiated,
  trackPaymentCompleted,
  trackScoreViewed,
  trackCTAClicked,
  trackHeroCTAClick,
};
